// B-105 — "Facturación al banco", in Reportes: how many transactions are billed to the bank, per office
// and concept, for a month or a range (366 days at most). The backend sums them
// (`getCierreBillingSummary`) from the concepts the client's YAML bills — the provision and the notes are
// not — over the offices this person sees, and lists the business days with no closing, which a bill must
// not skip in silence. The table shows the TX or the value, with totals per office and per concept; the
// Excel has both.
//
// Drawn only for a client that bills: for any other the route answers 404 and nothing is shown.
import { useEffect, useMemo, useState } from "react";
import { Building2, CalendarX2, Coins, Receipt, Repeat2 } from "lucide-react";
import { Button, Spinner, cn } from "@mantis-sas/ui";

import { getCierreBillingSummary } from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { fechaDMY, montoLegible } from "../../../utils/cierrePanel";
import { Cifra, enPalabras } from "./CierresVisual";
import { descargarTablas } from "../../../utils/excelTabla";
import { guia } from "../../../utils/cierreTours";

const TH = "px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground";
const TD = "px-3 py-2.5 tabular-nums";
const CAMPO = "h-8 rounded-[8px] border bg-card px-2 text-[12px]";

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
/** `2026-10-05` → `05 oct`, for a chip; the year is the period's. */
const fechaCorta = (iso) => {
  const [, m, d] = String(iso).split("-");
  return `${d} ${MESES_CORTOS[Number(m) - 1] || m}`;
};

/**
 * A figure of the bill: an icon, what it is, the figure and one line of context. `destacada` is the hero.
 * Drawn by `CierresVisual#Cifra`, so the dark card's text is white and a large figure fits its card.
 */
const Metrica = (props) => <Cifra {...props} />;

/** The month's first and last day: `2026-09` → `2026-09-01`, `2026-09-30`. */
export function rangoDeMes(mes) {
  const [y, m] = mes.split("-").map(Number);
  const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { desde: `${mes}-01`, hasta: `${mes}-${String(ultimo).padStart(2, "0")}` };
}

const mesActual = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Bogota" }).slice(0, 7);

/** The rows as an office × concept table: `{ oficinas: [{ code, name, celdas: {key: {tx, valor}}, tx, valor }] }`. Pure. */
export function tablaDeFacturacion(datos) {
  const porOficina = new Map();
  (datos?.rows || []).forEach((fila) => {
    if (!porOficina.has(fila.officeCode)) {
      porOficina.set(fila.officeCode, { code: fila.officeCode, name: fila.officeName, celdas: {}, tx: 0, valor: 0 });
    }
    const oficina = porOficina.get(fila.officeCode);
    oficina.celdas[fila.conceptKey] = { tx: fila.tx, valor: Number(fila.valor) };
    oficina.tx += fila.tx;
    oficina.valor += Number(fila.valor);
  });
  return { oficinas: [...porOficina.values()] };
}

/**
 * The Excel as tables (`descargarTablas`): one sheet with the TX, one with the values, each with its
 * totals, and the days with no closing. Pure.
 */
export function hojasDeFacturacion(datos, periodo = "") {
  const { oficinas } = tablaDeFacturacion(datos);
  const conceptos = datos.concepts || [];
  const totales = datos.totals?.porConcepto || [];
  const hoja = (campo, nombre) => ({
    nombre,
    titulo: `Facturación al banco · ${nombre}`,
    subtitulo: periodo,
    columnas: [
      { titulo: "Código", ancho: 10 },
      { titulo: "Oficina", ancho: 26 },
      ...conceptos.map((c) => ({ titulo: c.label, formato: campo === "tx" ? "count" : "money", ancho: campo === "tx" ? 13 : 18 })),
      { titulo: "Total", formato: campo === "tx" ? "count" : "money", ancho: campo === "tx" ? 12 : 20 },
    ],
    filas: oficinas.map((o) => [o.code, o.name, ...conceptos.map((c) => (o.celdas[c.key] ? o.celdas[c.key][campo] : 0)), o[campo]]),
    totales: ["", "Total", ...conceptos.map((c) => Number((totales.find((t) => t.conceptKey === c.key) || {})[campo] || 0)), Number(datos.totals?.[campo] || 0)],
  });
  const hojas = [hoja("tx", "TX"), hoja("valor", "Valor")];
  if (datos.diasFaltantes?.length) {
    hojas.push({
      nombre: "Días sin cierre", titulo: "Días hábiles sin cierre", subtitulo: periodo, columnas: [{ titulo: "Fecha", ancho: 14 }], filas: datos.diasFaltantes.map((d) => [fechaDMY(d)]),
    });
  }
  return hojas;
}

export function FacturacionBanco({ token, mantisBusinessId, clientCode, puedeExportar }) {
  const [modo, setModo] = useState("mes");
  const [mes, setMes] = useState(mesActual);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [ver, setVer] = useState("tx");
  const [datos, setDatos] = useState(null);
  const [disponible, setDisponible] = useState(true);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const rango = modo === "mes" ? (mes ? rangoDeMes(mes) : null) : (desde && hasta ? { desde, hasta } : null);

  useEffect(() => {
    let activo = true;
    if (!clientCode || !rango) return undefined;
    setCargando(true);
    setError(null);
    getCierreBillingSummary(token, mantisBusinessId, clientCode, { from: rango.desde, to: rango.hasta })
      .then((res) => {
        if (!activo) return;
        if (res?.success) setDatos(res.data);
        else if (res?.errorCode === "NOT_FOUND") setDisponible(false);
        else setError(correctionError(res) || "No se pudo cargar la facturación.");
      })
      .catch(() => activo && setError("No se pudo cargar la facturación."))
      .finally(() => activo && setCargando(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mantisBusinessId, clientCode, rango?.desde, rango?.hasta]);

  const tabla = useMemo(() => tablaDeFacturacion(datos), [datos]);
  if (!disponible) return null;
  const conceptos = datos?.concepts || [];
  const celda = (valor) => (ver === "tx" ? (valor ?? 0).toLocaleString("es-CO") : montoLegible(valor ?? 0));
  const total = (key) => (datos?.totals?.porConcepto || []).find((t) => t.conceptKey === key) || {};

  const exportar = () => {
    const periodo = `Del ${fechaDMY(rango.desde)} al ${fechaDMY(rango.hasta)}`;
    Promise.resolve(descargarTablas(`Facturacion ${clientCode} ${fechaDMY(rango.desde)} a ${fechaDMY(rango.hasta)}.xlsx`, hojasDeFacturacion(datos, periodo)))
      .catch(() => setError("No se pudo generar el Excel."));
  };

  const faltantes = datos?.diasFaltantes || [];
  const conMovimiento = tabla.oficinas.filter((o) => o.tx > 0).length;

  return (
    <section {...guia("Facturación al banco", "Las transacciones que se le facturan al banco por oficina y concepto, en un mes o un rango, en cantidad o en valor. \"Exportar Excel\" descarga la tabla.")} className="overflow-hidden rounded-[14px] border bg-card shadow-brinks-card" aria-label="Facturación al banco">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-brinks-surface text-brinks-foreground">
            <Receipt className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="text-[14px] font-semibold text-brinks-foreground">Facturación al banco</h2>
            <p className="mt-0.5 text-[12px] text-brinks-muted-foreground">Transacciones facturables por oficina y concepto (sin provisión ni notas).</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <select aria-label="Periodo" className={CAMPO} value={modo} onChange={(e) => setModo(e.target.value)}>
            <option value="mes">Mes</option>
            <option value="rango">Rango</option>
          </select>
          {modo === "mes" ? (
            <input aria-label="Mes" type="month" className={CAMPO} value={mes} onChange={(e) => setMes(e.target.value)} />
          ) : (
            <>
              <input aria-label="Desde" type="date" className={CAMPO} value={desde} onChange={(e) => setDesde(e.target.value)} />
              <input aria-label="Hasta" type="date" className={CAMPO} value={hasta} onChange={(e) => setHasta(e.target.value)} />
            </>
          )}
          <div className="inline-flex rounded-[8px] bg-brinks-surface p-0.5" role="group" aria-label="Ver">
            {[
              ["tx", "TX"],
              ["valor", "Valor"],
            ].map(([valor, etiqueta]) => (
              <button
                key={valor}
                type="button"
                aria-pressed={ver === valor}
                onClick={() => setVer(valor)}
                className={cn(
                  "h-7 rounded-[6px] px-3 text-[12px] font-medium transition",
                  ver === valor ? "bg-card text-brinks-foreground shadow-sm" : "text-brinks-muted-foreground hover:text-brinks-foreground"
                )}
              >
                {etiqueta}
              </button>
            ))}
          </div>
          {puedeExportar ? (
            <Button variant="outline" size="sm" className="h-8" disabled={!datos || !tabla.oficinas.length} onClick={exportar}>Exportar Excel</Button>
          ) : null}
        </div>
      </header>

      {cargando ? <div className="flex justify-center py-8"><Spinner /></div> : null}
      {error ? <p role="alert" className="px-4 py-3 text-[12px] text-brinks-danger">{error}</p> : null}

      {!cargando && datos ? (
        <div className="space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Metrica
              destacada
              icono={Repeat2}
              etiqueta="TX facturables"
              valor={(datos.totals?.tx || 0).toLocaleString("es-CO")}
              palabras={enPalabras(datos.totals?.tx || 0)}
              detalle={conMovimiento ? `≈ ${Math.round((datos.totals?.tx || 0) / conMovimiento).toLocaleString("es-CO")} por oficina con movimiento` : "Sin transacciones en el periodo"}
            />
            <Metrica
              icono={Coins}
              etiqueta="Valor facturado"
              valor={montoLegible(datos.totals?.valor || 0)}
              palabras={enPalabras(datos.totals?.valor || 0, { pesos: true })}
              detalle={datos.totals?.tx ? `Ticket promedio ${montoLegible(Math.round(Number(datos.totals?.valor || 0) / datos.totals.tx))}` : "—"}
            />
            <Metrica
              icono={Building2}
              etiqueta="Oficinas"
              valor={tabla.oficinas.length}
              detalle={`${conMovimiento} con transacciones en el periodo`}
            />
            <Metrica
              icono={CalendarX2}
              etiqueta="Días sin cierre"
              valor={faltantes.length}
              detalle={faltantes.length ? "Hábiles del periodo que la factura no cubre" : "Todos los días hábiles tienen cierre"}
              tono={faltantes.length ? "bg-brinks-warning-soft text-brinks-warning" : "bg-brinks-success-soft text-brinks-success"}
            />
          </div>
          {faltantes.length ? (
            <div role="status" aria-label="Días hábiles sin cierre" className="rounded-[14px] border border-brinks-warning-border bg-brinks-warning-soft p-3">
              <p className="flex items-center gap-2 text-[12px] font-semibold text-brinks-warning">
                <CalendarX2 className="h-4 w-4" />
                {faltantes.length} {faltantes.length === 1 ? "día hábil" : "días hábiles"} sin cierre
                <span className="font-normal opacity-80">— la factura no los incluye hasta que se procesen</span>
              </p>
              <ul className="mt-2.5 flex max-h-24 list-none flex-wrap gap-1.5 overflow-y-auto p-0">
                {faltantes.map((d) => (
                  <li
                    key={d}
                    title={fechaDMY(d)}
                    className="rounded-full border border-brinks-warning-border bg-card px-2.5 py-0.5 text-[11.5px] font-medium tabular-nums text-brinks-warning"
                  >
                    {fechaCorta(d)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {tabla.oficinas.length ? (
            // Long: 47 offices for Banco Agrario. It scrolls inside its own box, with the header,
            // the office column and the totals row pinned.
            <div className="max-h-[440px] overflow-auto rounded-[12px] border">
              <table className="w-max min-w-full border-separate border-spacing-0 text-[12px]" aria-label="Facturación por oficina y concepto">
                <thead>
                  <tr className="text-left">
                    <th className={cn(TH, "sticky left-0 top-0 z-30 border-b bg-brinks-surface")}>Oficina</th>
                    {conceptos.map((c) => <th key={c.key} className={cn(TH, "sticky top-0 z-20 border-b bg-brinks-surface text-right")}>{c.label}</th>)}
                    <th className={cn(TH, "sticky top-0 z-20 border-b bg-brinks-surface text-right")}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {tabla.oficinas.map((o) => (
                    <tr key={o.code} className="group">
                      <td className={cn(TD, "sticky left-0 z-10 whitespace-nowrap border-b bg-card group-hover:bg-brinks-surface-hover")}>{o.code} {o.name}</td>
                      {conceptos.map((c) => <td key={c.key} className={cn(TD, "border-b text-right group-hover:bg-brinks-surface-hover")}>{celda(o.celdas[c.key] && o.celdas[c.key][ver])}</td>)}
                      <td className={cn(TD, "border-b text-right font-semibold group-hover:bg-brinks-surface-hover")}>{celda(o[ver])}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td className={cn(TD, "sticky bottom-0 left-0 z-30 bg-brinks-surface shadow-[inset_0_1px_0_var(--brinks-border)]")}>Total</td>
                    {conceptos.map((c) => <td key={c.key} className={cn(TD, "sticky bottom-0 z-20 bg-brinks-surface text-right shadow-[inset_0_1px_0_var(--brinks-border)]")}>{celda(ver === "tx" ? total(c.key).tx : Number(total(c.key).valor || 0))}</td>)}
                    <td className={cn(TD, "sticky bottom-0 z-20 bg-brinks-surface text-right shadow-[inset_0_1px_0_var(--brinks-border)]")}>{celda(ver === "tx" ? datos.totals?.tx : Number(datos.totals?.valor || 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="rounded-[12px] bg-brinks-surface px-3 py-6 text-center text-[12px] text-brinks-muted-foreground">No hay transacciones en este periodo.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}

export default FacturacionBanco;
